import React, { useState } from 'react';
import { Patient, Diagnosis, Vital } from '../../../types';
import { formatDate } from '../utils/patientUtils';

interface ClinicalTabProps {
  patient: Patient;
}

/**
 * Clinical tab for patient details - shows clinical notes, care plans, etc.
 */
const ClinicalTab: React.FC<ClinicalTabProps> = ({ patient }) => {
  const [activeSection, setActiveSection] = useState<string>('notes');

  return (
    <div className="space-y-6">
      {/* Clinical Navigation */}
      <div role="tablist" className="tabs tabs-bordered mb-6">
        <button 
          role="tab"
          onClick={() => setActiveSection('notes')} 
          className={`tab ${activeSection === 'notes' ? 'tab-active' : ''}`}
        >
          Clinical Notes
        </button>
        <button 
          role="tab"
          onClick={() => setActiveSection('problems')} 
          className={`tab ${activeSection === 'problems' ? 'tab-active' : ''}`}
        >
          Problem List
        </button>
        <button 
          role="tab"
          onClick={() => setActiveSection('medications')} 
          className={`tab ${activeSection === 'medications' ? 'tab-active' : ''}`}
        >
          Medications
        </button>
        <button 
          role="tab"
          onClick={() => setActiveSection('allergies')} 
          className={`tab ${activeSection === 'allergies' ? 'tab-active' : ''}`}
        >
          Allergies
        </button>
        <button 
          role="tab"
          onClick={() => setActiveSection('vitals')} 
          className={`tab ${activeSection === 'vitals' ? 'tab-active' : ''}`}
        >
          Vitals
        </button>
      </div>

      {/* Action Button */}
      <div className="flex justify-end">
        <button className="btn btn-primary">
          {activeSection === 'notes' && 'New Clinical Note'}
          {activeSection === 'problems' && 'Add Problem'}
          {activeSection === 'medications' && 'Prescribe Medication'}
          {activeSection === 'allergies' && 'Add Allergy'}
          {activeSection === 'vitals' && 'Record Vitals'}
        </button>
      </div>

      {/* Content based on active section */}
      {activeSection === 'notes' && (
        <div className="bg-base-100 rounded-lg shadow-md p-6">
          <h3 className="text-lg font-medium text-base-content mb-4">Clinical Notes</h3>
          
          {patient.clinical_notes && patient.clinical_notes.length > 0 ? (
            <div className="space-y-6">
              {patient.clinical_notes
                .sort((a, b) => new Date(b.created_at).getTime() - new Date(a.created_at).getTime())
                .map((note, index) => (
                  <div key={note.id || index} className="border-b pb-6 last:border-b-0 last:pb-0">
                    <div className="flex justify-between items-start mb-2">
                      <div>
                        <h4 className="font-medium">{note.title || 'Clinical Note'}</h4>
                        <p className="text-sm text-base-content opacity-60">
                          {formatDate(note.created_at)} by {note.created_by_name || 'Unknown Provider'}
                        </p>
                      </div>
                      <div className="flex space-x-2">
                        <button className="text-base-content opacity-50 hover:opacity-70">
                          <svg className="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                          </svg>
                        </button>
                        <button className="text-base-content opacity-50 hover:opacity-70">
                          <svg className="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                          </svg>
                        </button>
                      </div>
                    </div>
                    <div className="prose max-w-none">
                      <p className="text-base-content opacity-80">{note.content || 'No details provided'}</p>
                      {note.assessment && (
                        <div className="mt-3">
                          <h5 className="text-sm font-medium text-base-content opacity-80">Assessment</h5>
                          <p className="text-base-content opacity-70">{note.assessment}</p>
                        </div>
                      )}
                      {note.plan && (
                        <div className="mt-3">
                          <h5 className="text-sm font-medium text-base-content opacity-80">Plan</h5>
                          <p className="text-base-content opacity-70">{note.plan}</p>
                        </div>
                      )}
                    </div>
                    <div className="mt-3 flex flex-wrap gap-2">
                      {note.tags && note.tags.map((tag: string, tagIndex: number) => (
                        <span 
                          key={tagIndex}
                          className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800"
                        >
                          {tag}
                        </span>
                      ))}
                    </div>
                  </div>
                ))
              }
            </div>
          ) : (
            <div className="text-center py-12">
              <svg className="mx-auto h-12 w-12 text-base-content opacity-50" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
              </svg>
              <h3 className="mt-2 text-sm font-medium text-base-content">No Clinical Notes</h3>
              <p className="mt-1 text-sm text-base-content opacity-60">Get started by creating a new clinical note.</p>
              <div className="mt-6">
                <button
                  type="button"
                  className="btn btn-primary"
                >
                  <svg className="-ml-1 mr-2 h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 4v16m8-8H4" />
                  </svg>
                  New Clinical Note
                </button>
              </div>
            </div>
          )}
        </div>
      )}
      
      {activeSection === 'problems' && (
        <div className="bg-base-100 rounded-lg shadow-md p-6">
          <h3 className="text-lg font-medium text-base-content mb-4">Problem List</h3>
          
          {patient.diagnoses && patient.diagnoses.length > 0 ? (
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-base-200">
                  <tr>
                    <th className="px-4 py-3 text-left text-xs font-medium text-base-content opacity-60 uppercase tracking-wider">Problem</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-base-content opacity-60 uppercase tracking-wider">Code</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-base-content opacity-60 uppercase tracking-wider">Onset</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-base-content opacity-60 uppercase tracking-wider">Status</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-base-content opacity-60 uppercase tracking-wider">Provider</th>
                    <th className="px-4 py-3 text-right text-xs font-medium text-base-content opacity-60 uppercase tracking-wider">Actions</th>
                  </tr>
                </thead>
                <tbody className="bg-base-100 divide-y divide-gray-200">
                  {patient.diagnoses.map((problem: Diagnosis, index: number) => (
                    <tr key={problem.id || index} className={index % 2 === 0 ? 'bg-base-200' : 'bg-base-100'}>
                      <td className="px-4 py-3 whitespace-nowrap">
                        <div className="text-sm font-medium text-base-content">{problem.name}</div>
                        {problem.notes && <div className="text-xs text-base-content opacity-60">{problem.notes}</div>}
                      </td>
                      <td className="px-4 py-3 whitespace-nowrap text-sm text-base-content opacity-60">
                        {problem.code || 'No code'}
                      </td>
                      <td className="px-4 py-3 whitespace-nowrap text-sm text-base-content opacity-60">
                        {formatDate(problem.onset_date)}
                      </td>
                      <td className="px-4 py-3 whitespace-nowrap">
                        <span className={`px-2 py-1 inline-flex text-xs leading-5 font-semibold rounded-full ${
                          problem.status === 'active' ? 'bg-green-100 text-green-800' :
                          problem.status === 'resolved' ? 'bg-gray-100 text-gray-800' :
                          'bg-yellow-100 text-yellow-800'
                        }`}>
                          {problem.status || 'Active'}
                        </span>
                      </td>
                      <td className="px-4 py-3 whitespace-nowrap text-sm text-base-content opacity-60">
                        Not assigned
                      </td>
                      <td className="px-4 py-3 whitespace-nowrap text-right text-sm font-medium">
                        <button className="text-blue-600 hover:text-blue-900 mr-3">Edit</button>
                        <button className="text-red-600 hover:text-red-900">Remove</button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : (
            <div className="text-center py-12">
              <svg className="mx-auto h-12 w-12 text-base-content opacity-50" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
              </svg>
              <h3 className="mt-2 text-sm font-medium text-base-content">No Problems Listed</h3>
              <p className="mt-1 text-sm text-base-content opacity-60">Get started by adding a new problem.</p>
              <div className="mt-6">
                <button
                  type="button"
                  className="btn btn-primary"
                >
                  <svg className="-ml-1 mr-2 h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 4v16m8-8H4" />
                  </svg>
                  Add Problem
                </button>
              </div>
            </div>
          )}
        </div>
      )}
      
      {activeSection === 'medications' && (
        <div className="bg-base-100 rounded-lg shadow-md p-6">
          <h3 className="text-lg font-medium text-base-content mb-4">Medications</h3>
          
          {patient.medications && patient.medications.length > 0 ? (
            <div className="space-y-4">
              <div className="flex flex-wrap gap-2 mb-4">
                <button className="px-3 py-1 text-sm bg-blue-100 text-blue-700 rounded-full">
                  All
                </button>
                <button className="px-3 py-1 text-sm bg-gray-100 text-base-content opacity-80 rounded-full">
                  Active
                </button>
                <button className="px-3 py-1 text-sm bg-gray-100 text-base-content opacity-80 rounded-full">
                  Discontinued
                </button>
              </div>
              
              <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-gray-200">
                  <thead className="bg-base-200">
                    <tr>
                      <th className="px-4 py-3 text-left text-xs font-medium text-base-content opacity-60 uppercase tracking-wider">Medication</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-base-content opacity-60 uppercase tracking-wider">Dosage</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-base-content opacity-60 uppercase tracking-wider">Frequency</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-base-content opacity-60 uppercase tracking-wider">Started</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-base-content opacity-60 uppercase tracking-wider">Status</th>
                      <th className="px-4 py-3 text-right text-xs font-medium text-base-content opacity-60 uppercase tracking-wider">Actions</th>
                    </tr>
                  </thead>
                  <tbody className="bg-base-100 divide-y divide-gray-200">
                    {patient.medications.map((medication, index) => (
                      <tr key={medication.id || index} className={index % 2 === 0 ? 'bg-base-200' : 'bg-base-100'}>
                        <td className="px-4 py-3">
                          <div className="text-sm font-medium text-base-content">{medication.name}</div>
                          {medication.notes && <div className="text-xs text-base-content opacity-60">{medication.notes}</div>}
                        </td>
                        <td className="px-4 py-3 whitespace-nowrap text-sm text-base-content opacity-60">
                          {medication.dosage || 'Not specified'}
                        </td>
                        <td className="px-4 py-3 whitespace-nowrap text-sm text-base-content opacity-60">
                          {medication.frequency || 'Not specified'}
                        </td>
                        <td className="px-4 py-3 whitespace-nowrap text-sm text-base-content opacity-60">
                          {formatDate(medication.start_date)}
                        </td>
                        <td className="px-4 py-3 whitespace-nowrap">
                          <span className={`px-2 py-1 inline-flex text-xs leading-5 font-semibold rounded-full ${
                            medication.is_active ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-800'
                          }`}>
                            {medication.is_active ? 'Active' : 'Discontinued'}
                          </span>
                        </td>
                        <td className="px-4 py-3 whitespace-nowrap text-right text-sm font-medium">
                          <button className="text-blue-600 hover:text-blue-900 mr-3">
                            {medication.is_active ? 'Discontinue' : 'Reactivate'}
                          </button>
                          <button className="text-blue-600 hover:text-blue-900">
                            Details
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          ) : (
            <div className="text-center py-12">
              <svg className="mx-auto h-12 w-12 text-base-content opacity-50" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
              </svg>
              <h3 className="mt-2 text-sm font-medium text-base-content">No Medications</h3>
              <p className="mt-1 text-sm text-base-content opacity-60">This patient currently has no medications on file.</p>
              <div className="mt-6">
                <button
                  type="button"
                  className="btn btn-primary"
                >
                  <svg className="-ml-1 mr-2 h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 4v16m8-8H4" />
                  </svg>
                  Prescribe Medication
                </button>
              </div>
            </div>
          )}
        </div>
      )}
      
      {activeSection === 'allergies' && (
        <div className="bg-base-100 rounded-lg shadow-md p-6">
          <h3 className="text-lg font-medium text-base-content mb-4">Allergies</h3>
          
          {patient.allergies && patient.allergies.length > 0 ? (
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-base-200">
                  <tr>
                    <th className="px-4 py-3 text-left text-xs font-medium text-base-content opacity-60 uppercase tracking-wider">Allergen</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-base-content opacity-60 uppercase tracking-wider">Reaction</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-base-content opacity-60 uppercase tracking-wider">Severity</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-base-content opacity-60 uppercase tracking-wider">Onset Date</th>
                    <th className="px-4 py-3 text-right text-xs font-medium text-base-content opacity-60 uppercase tracking-wider">Actions</th>
                  </tr>
                </thead>
                <tbody className="bg-base-100 divide-y divide-gray-200">
                  {patient.allergies.map((allergy, index) => {
                    const allergyObj = typeof allergy === 'string' ? { name: allergy } : allergy;
                    return (
                      <tr key={allergyObj.id || index} className={index % 2 === 0 ? 'bg-base-200' : 'bg-base-100'}>
                        <td className="px-4 py-3 whitespace-nowrap text-sm font-medium text-base-content">
                          {allergyObj.name}
                        </td>
                        <td className="px-4 py-3 whitespace-nowrap text-sm text-base-content opacity-60">
                          {allergyObj.reaction || 'Not specified'}
                        </td>
                        <td className="px-4 py-3 whitespace-nowrap">
                          <span className={`px-2 py-1 inline-flex text-xs leading-5 font-semibold rounded-full ${
                            allergyObj.severity === 'Severe' || allergyObj.severity === 'severe' ? 'bg-red-100 text-red-800' :
                            allergyObj.severity === 'Moderate' || allergyObj.severity === 'moderate' ? 'bg-yellow-100 text-yellow-800' :
                            'bg-green-100 text-green-800'
                          }`}>
                            {allergyObj.severity || 'Not specified'}
                          </span>
                        </td>
                        <td className="px-4 py-3 whitespace-nowrap text-sm text-base-content opacity-60">
                          {formatDate(allergyObj.onset_date) || 'Not specified'}
                        </td>
                        <td className="px-4 py-3 whitespace-nowrap text-right text-sm font-medium">
                          <button className="text-blue-600 hover:text-blue-900 mr-3">Edit</button>
                          <button className="text-red-600 hover:text-red-900">Remove</button>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          ) : (
            <div className="text-center py-12">
              <svg className="mx-auto h-12 w-12 text-base-content opacity-50" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
              <h3 className="mt-2 text-sm font-medium text-base-content">
                {patient.no_known_allergies ? 'No Known Allergies (NKA)' : 'No Allergies Recorded'}
              </h3>
              <p className="mt-1 text-sm text-base-content opacity-60">
                {patient.no_known_allergies 
                  ? 'Patient has no known allergies' 
                  : 'Allergies have not been documented for this patient'
                }
              </p>
              <div className="mt-6">
                <button
                  type="button"
                  className="btn btn-primary"
                >
                  <svg className="-ml-1 mr-2 h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 4v16m8-8H4" />
                  </svg>
                  Add Allergy
                </button>
              </div>
            </div>
          )}
        </div>
      )}
      
      {activeSection === 'vitals' && (
        <div className="bg-base-100 rounded-lg shadow-md p-6">
          <h3 className="text-lg font-medium text-base-content mb-4">Vital Signs</h3>
          
          {patient.vitals && patient.vitals.length > 0 ? (
            <div className="space-y-6">
              {/* Latest vitals */}
              <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
                <div className="bg-blue-50 rounded-lg p-4">
                  <p className="text-sm text-blue-700">Blood Pressure</p>
                  <p className="text-xl font-bold text-blue-700">
                    {patient.vitals[0].blood_pressure || 'N/A'}
                  </p>
                  <p className="text-xs text-blue-600 mt-1">mmHg</p>
                </div>
                
                <div className="bg-green-50 rounded-lg p-4">
                  <p className="text-sm text-green-700">Heart Rate</p>
                  <p className="text-xl font-bold text-green-700">
                    {patient.vitals[0].heart_rate || 'N/A'}
                  </p>
                  <p className="text-xs text-green-600 mt-1">bpm</p>
                </div>
                
                <div className="bg-yellow-50 rounded-lg p-4">
                  <p className="text-sm text-yellow-700">Resp Rate</p>
                  <p className="text-xl font-bold text-yellow-700">
                    {patient.vitals[0].respiratory_rate || 'N/A'}
                  </p>
                  <p className="text-xs text-yellow-600 mt-1">bpm</p>
                </div>
                
                <div className="bg-purple-50 rounded-lg p-4">
                  <p className="text-sm text-purple-700">Temperature</p>
                  <p className="text-xl font-bold text-purple-700">
                    {patient.vitals[0].temperature || 'N/A'}
                  </p>
                  <p className="text-xs text-purple-600 mt-1">°C</p>
                </div>
                
                <div className="bg-indigo-50 rounded-lg p-4">
                  <p className="text-sm text-indigo-700">O₂ Saturation</p>
                  <p className="text-xl font-bold text-indigo-700">
                    {patient.vitals[0].oxygen_saturation || 'N/A'}
                  </p>
                  <p className="text-xs text-indigo-600 mt-1">%</p>
                </div>
              </div>
              
              {/* Vitals history */}
              <div>
                <h4 className="font-medium text-base-content opacity-80 mb-2">Vitals History</h4>
                <div className="overflow-x-auto">
                  <table className="min-w-full divide-y divide-gray-200">
                    <thead className="bg-base-200">
                      <tr>
                        <th className="px-4 py-3 text-left text-xs font-medium text-base-content opacity-60 uppercase tracking-wider">Date</th>
                        <th className="px-4 py-3 text-left text-xs font-medium text-base-content opacity-60 uppercase tracking-wider">BP</th>
                        <th className="px-4 py-3 text-left text-xs font-medium text-base-content opacity-60 uppercase tracking-wider">HR</th>
                        <th className="px-4 py-3 text-left text-xs font-medium text-base-content opacity-60 uppercase tracking-wider">Resp</th>
                        <th className="px-4 py-3 text-left text-xs font-medium text-base-content opacity-60 uppercase tracking-wider">Temp</th>
                        <th className="px-4 py-3 text-left text-xs font-medium text-base-content opacity-60 uppercase tracking-wider">O₂</th>
                        <th className="px-4 py-3 text-left text-xs font-medium text-base-content opacity-60 uppercase tracking-wider">Provider</th>
                      </tr>
                    </thead>
                    <tbody className="bg-base-100 divide-y divide-gray-200">
                      {patient.vitals.map((vital: Vital, index: number) => (
                        <tr key={vital.id || index} className={index % 2 === 0 ? 'bg-base-200' : 'bg-base-100'}>
                          <td className="px-4 py-3 whitespace-nowrap text-sm text-base-content opacity-60">
                            {formatDate(vital.recorded_date)}
                          </td>
                          <td className="px-4 py-3 whitespace-nowrap text-sm text-base-content">
                            {vital.blood_pressure || 'N/A'}
                          </td>
                          <td className="px-4 py-3 whitespace-nowrap text-sm text-base-content">
                            {vital.heart_rate || 'N/A'}
                          </td>
                          <td className="px-4 py-3 whitespace-nowrap text-sm text-base-content">
                            {vital.respiratory_rate || 'N/A'}
                          </td>
                          <td className="px-4 py-3 whitespace-nowrap text-sm text-base-content">
                            {vital.temperature || 'N/A'}
                          </td>
                          <td className="px-4 py-3 whitespace-nowrap text-sm text-base-content">
                            {vital.oxygen_saturation || 'N/A'}
                          </td>
                          <td className="px-4 py-3 whitespace-nowrap text-sm text-base-content opacity-60">
                            {vital.recorded_by || 'Unknown'}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            </div>
          ) : (
            <div className="text-center py-12">
              <svg className="mx-auto h-12 w-12 text-base-content opacity-50" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
              </svg>
              <h3 className="mt-2 text-sm font-medium text-base-content">No Vitals</h3>
              <p className="mt-1 text-sm text-base-content opacity-60">No vital signs have been recorded for this patient.</p>
              <div className="mt-6">
                <button
                  type="button"
                  className="btn btn-primary"
                >
                  <svg className="-ml-1 mr-2 h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 4v16m8-8H4" />
                  </svg>
                  Record Vitals
                </button>
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default ClinicalTab;
